import React, { useEffect } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Chip,
  Avatar,
  Autocomplete,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Collapse,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { X, Plus, Minus, User, Users, Eye, MessageSquare, ArrowLeft, Repeat, ChevronDown, ChevronUp, Settings2, Info } from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { clearTimerForTask } from '@/store/slices/timerSlice';
import { TaskPriority, TaskStatus, Task, TaskRACI, Milestone, TaskCategory, RecurrenceFrequency, RecurrenceEndType } from '@/types';
import { taskService, projectService, userService, configService, enumMasterService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { isAdminOrManager } from '@/shared/utils/roles';
import type { User as UserType } from '@/types';
import SubtasksList, { SubtaskData } from './SubtasksList';
import ManageTaskCategoriesModal from './ManageTaskCategoriesModal';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';
import {
  DEFAULT_WORK_HOURS_PER_DAY,
  calculateEstimatedHoursFromDates,
} from '@/shared/utils/projectDuration';

// Create dynamic schema based on config
const createTaskSchema = (maxEstimatedMinutes: number) => z.object({
  title: z.string().min(1, 'Title is required').max(200, 'Title must be 200 characters or less'),
  description: z.string().max(2000, 'Description too long').optional(),
  priority: z.nativeEnum(TaskPriority),
  category: z.string().optional(),
  startDate: z.any().nullable().optional(),
  dueDate: z.any().refine((v) => v != null && dayjs(v).isValid(), { message: 'Due date is required' }),
  estimatedTime: z.number().min(0, 'Must be positive').max(maxEstimatedMinutes, `Max ${Math.floor(maxEstimatedMinutes / 60)} hours`).optional().nullable(),
  projectId: z.string().optional().nullable(),
  milestoneId: z.string().optional().nullable(),
  isOnsite: z.boolean().optional(),
  onsiteLocation: z.string().max(200, 'Location too long').optional(),
  raci: z.object({
    responsible: z.array(z.string()).min(1, 'Select at least one responsible'),
    accountable: z.string().min(1, 'Select one accountable person'),
    consulted: z.array(z.string()).default([]),
    informed: z.array(z.string()).default([]),
  }),
});

// Default schema for initial setup
const taskSchema = createTaskSchema(480);

type TaskFormData = z.infer<typeof taskSchema>;

type EstimatedTimeUnit = 'minutes' | 'hours';

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  initialDueDate?: string;
  projectEstimatedMinutes?: number;
  projectStartDate?: string;
  projectEndDate?: string;
  projectAllocatedMinutes?: number;
  onSuccess?: () => void;
  initialData?: Record<string, unknown>;
}

function userDisplayName(u: UserType): string {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

/**
 * CreateTaskModal
 * Comprehensive task creation form with RACI assignment
 * Mobile-first responsive design
 * Follows react-hook-form + zod pattern for validation
 */
const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  onClose,
  projectId: initialProjectId,
  initialDueDate,
  projectEstimatedMinutes,
  projectStartDate,
  projectEndDate,
  projectAllocatedMinutes,
  onSuccess,
  initialData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEditMode = Boolean(initialData);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const { showError, showSuccess } = useSnackbar();

  // State for "Create & Add Another" functionality and subtasks
  const [createAndContinue, setCreateAndContinue] = React.useState(false);
  const [subtasks, setSubtasks] = React.useState<SubtaskData[]>([]);
  const [subtasksExpanded, setSubtasksExpanded] = React.useState(false);

  // State for recurring tasks
  const [enableRecurrence, setEnableRecurrence] = React.useState(false);
  const [recurrenceExpanded, setRecurrenceExpanded] = React.useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = React.useState<RecurrenceFrequency>(RecurrenceFrequency.DAILY);
  const [recurrenceInterval, setRecurrenceInterval] = React.useState(1);
  const [recurrenceDaysOfWeek, setRecurrenceDaysOfWeek] = React.useState<number[]>([]);
  const [recurrenceEndType, setRecurrenceEndType] = React.useState<RecurrenceEndType>(RecurrenceEndType.NEVER);
  const [manageCategoriesOpen, setManageCategoriesOpen] = React.useState(false);
  const [estimatedTimeUnit, setEstimatedTimeUnit] = React.useState<EstimatedTimeUnit>('hours');
  // Local draft so backspace/typing works (minutes↔hours conversion must not fight the input)
  const [estimatedTimeDraft, setEstimatedTimeDraft] = React.useState<string | null>(null);

  const canManageCategories = isAdminOrManager(currentUser?.role);

  const { data: taskCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: QUERY_KEYS.taskCategories(),
    queryFn: () => enumMasterService.listTaskCategories(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch max estimated hours config (fall back to default if user cannot read admin config)
  const { data: maxEstimatedConfig } = useQuery({
    queryKey: ['config', 'task.max_estimated_hours'],
    queryFn: async () => {
      try {
        return await configService.getConfigByKey('task.max_estimated_hours');
      } catch {
        return null;
      }
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const maxEstimatedMinutes = React.useMemo(() => {
    if (!maxEstimatedConfig?.value) return 480;
    const value = typeof maxEstimatedConfig.value === 'number' 
      ? maxEstimatedConfig.value 
      : parseInt(String(maxEstimatedConfig.value), 10);
    return isNaN(value) ? 480 : value;
  }, [maxEstimatedConfig]);

  const workHoursPerDay = currentUser?.workHoursPerDay ?? DEFAULT_WORK_HOURS_PER_DAY;

  const projectBudgetContext = React.useMemo(() => {
    if (!initialProjectId || !projectEstimatedMinutes || projectEstimatedMinutes <= 0) {
      return null;
    }
    const allocated = projectAllocatedMinutes ?? 0;
    return {
      estimated: projectEstimatedMinutes,
      allocated,
      remaining: Math.max(0, projectEstimatedMinutes - allocated),
    };
  }, [initialProjectId, projectEstimatedMinutes, projectAllocatedMinutes]);

  const getDefaultEstimatedTime = React.useCallback(() => {
    if (projectBudgetContext && projectBudgetContext.remaining > 0) {
      return Math.min(projectBudgetContext.remaining, maxEstimatedMinutes);
    }
    if (projectStartDate && projectEndDate) {
      const hours = calculateEstimatedHoursFromDates(projectStartDate, projectEndDate, workHoursPerDay);
      if (hours) {
        return Math.min(Math.round(hours * 60), maxEstimatedMinutes);
      }
    }
    return 60;
  }, [projectBudgetContext, projectStartDate, projectEndDate, workHoursPerDay, maxEstimatedMinutes]);

  const maxEstimatedHours = React.useMemo(
    () => Math.round((maxEstimatedMinutes / 60) * 100) / 100,
    [maxEstimatedMinutes]
  );

  const formatMinutesForUnit = React.useCallback(
    (minutes?: number | null) => {
      if (minutes == null || Number.isNaN(minutes)) return '';
      if (estimatedTimeUnit === 'hours') {
        // Keep up to 2 decimals so typed hours like 18.17 display cleanly
        return Math.round((minutes / 60) * 100) / 100;
      }
      return minutes;
    },
    [estimatedTimeUnit]
  );

  const formatMinutesAsHoursAndMinutes = React.useCallback((minutes?: number | null) => {
    if (minutes == null || Number.isNaN(minutes) || minutes < 0) return null;
    const wholeHours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (wholeHours === 0) return `${mins}m`;
    if (mins === 0) return `${wholeHours}h`;
    return `${wholeHours}h ${mins}m`;
  }, []);

  const parseInputToMinutes = React.useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
        return null;
      }
      const num = Number(trimmed);
      if (Number.isNaN(num) || num < 0) return null;
      // Hours can be any decimal (e.g. 18.17); we store whole minutes
      return estimatedTimeUnit === 'hours' ? Math.round(num * 60) : Math.round(num);
    },
    [estimatedTimeUnit]
  );

  // Fetch projects for dropdown (pageSize 100 matches backend max)
  const { data: projectsData } = useQuery({
    queryKey: [...QUERY_KEYS.projects, 'task-dropdown'],
    queryFn: () => projectService.getProjects(undefined, 1, 100),
    enabled: open,
  });

  // Form setup (moved before queries to use watch)
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<TaskFormData>({
    resolver: zodResolver(createTaskSchema(maxEstimatedMinutes)),
    mode: 'onTouched',
    defaultValues: {
      title: '',
      description: '',
      priority: TaskPriority.P3,
      category: undefined,
      startDate: dayjs(),
      dueDate: dayjs().add(1, 'day'),
      estimatedTime: 60,
      projectId: initialProjectId || null,
      milestoneId: null,
      isOnsite: false,
      onsiteLocation: '',
      raci: {
        responsible: [],
        accountable: '',
        consulted: [],
        informed: [],
      },
    },
  });

  // Watch projectId changes to refetch RACI users
  const selectedProjectId = watch('projectId');
  
  // Determine which project to use for fetching RACI users
  const effectiveProjectId = React.useMemo(() => {
    if (isEditMode && initialData) {
      // Edit mode: Use the task's current projectId
      const task = initialData as unknown as Task;
      const projectId = task.projectId || undefined;
      return projectId;
    }
    // Create mode: Prioritize initialProjectId (when creating from project), then selectedProjectId (from dropdown)
    const projectId = initialProjectId || selectedProjectId || undefined;
    return projectId;
  }, [isEditMode, initialData, initialProjectId, selectedProjectId]);

  // Fetch users for RACI assignment from API
  const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['raci-assignable-users', effectiveProjectId],
    queryFn: () => userService.getRACIAssignableUsers(effectiveProjectId),
    enabled: open,
    staleTime: 0, // Always fetch fresh data when dialog opens
    refetchOnMount: 'always', // Refetch every time the modal opens
  });

  // In edit mode, fetch any RACI-assigned users who might not be in the project team
  const taskRaciUserIds = React.useMemo(() => {
    if (!isEditMode || !initialData) return [];
    const task = initialData as unknown as Task;
    const ids = new Set<string>();
    if (task.raci?.responsible) task.raci.responsible.forEach(id => ids.add(id));
    if (task.raci?.accountable) ids.add(task.raci.accountable);
    if (task.raci?.consulted) task.raci.consulted.forEach(id => ids.add(id));
    if (task.raci?.informed) task.raci.informed.forEach(id => ids.add(id));
    return Array.from(ids);
  }, [isEditMode, initialData]);

  const { data: additionalUsersData, isLoading: additionalUsersLoading } = useQuery({
    queryKey: ['raci-assigned-users', taskRaciUserIds],
    queryFn: async () => {
      if (taskRaciUserIds.length === 0) return [];
      // Fetch these specific users by ID
      const users = await Promise.all(
        taskRaciUserIds.map(id => userService.getUser(id).catch(() => null))
      );
      const fetchedUsers = users.filter(u => u !== null);
      return fetchedUsers;
    },
    enabled: open && isEditMode && taskRaciUserIds.length > 0,
  });

  const projects = projectsData?.items ?? [];

  const selectedProject = React.useMemo(
    () => projects.find((p: { id: string }) => p.id === selectedProjectId),
    [projects, selectedProjectId]
  );

  const projectDateRange = React.useMemo(() => {
    const startRaw = selectedProject?.startDate || (selectedProjectId === initialProjectId ? projectStartDate : undefined);
    const endRaw = selectedProject?.endDate || (selectedProjectId === initialProjectId ? projectEndDate : undefined);
    const start = startRaw && dayjs(startRaw).isValid() ? dayjs(startRaw).startOf('day') : null;
    const end = endRaw && dayjs(endRaw).isValid() ? dayjs(endRaw).startOf('day') : null;
    if (!start && !end) return null;
    return {
      start,
      end,
      label: [
        start ? start.format('MMM D, YYYY') : 'not set',
        end ? end.format('MMM D, YYYY') : 'not set',
      ].join(' – '),
    };
  }, [selectedProject, selectedProjectId, initialProjectId, projectStartDate, projectEndDate]);
  
  // Merge project users with additionally fetched RACI-assigned users
  const users = React.useMemo(() => {
    const projectUsers = usersData ?? [];
    const additionalUsers = additionalUsersData ?? [];

    const userMap = new Map<string, UserType>();
    projectUsers.forEach((user) => {
      userMap.set(user.id, user);
    });
    additionalUsers.forEach((user) => {
      userMap.set(user.id, user);
    });

    return Array.from(userMap.values());
  }, [usersData, additionalUsersData]);

  React.useEffect(() => {
    if (open && usersError) {
      console.error('[CreateTask] Error loading users:', usersError);
    }
  }, [open, usersError]);

  // Initialize create form only when the modal opens — not when the user picks a project.
  // Selecting a project refetches RACI users; those deps used to wipe projectId back to null.
  useEffect(() => {
    if (!open || isEditMode) return;
    setEnableRecurrence(false);
    setRecurrenceExpanded(false);
    setRecurrenceFrequency(RecurrenceFrequency.DAILY);
    setRecurrenceInterval(1);
    setRecurrenceDaysOfWeek([]);
    setRecurrenceEndType(RecurrenceEndType.NEVER);
    setEstimatedTimeDraft(null);
    reset({
      title: '',
      description: '',
      priority: TaskPriority.P3,
      category: undefined,
      startDate: projectStartDate ? dayjs(projectStartDate) : dayjs(),
      dueDate: projectEndDate
        ? dayjs(projectEndDate)
        : initialDueDate
          ? dayjs(initialDueDate)
          : dayjs().add(1, 'day'),
      estimatedTime: getDefaultEstimatedTime(),
      projectId: initialProjectId || null,
      milestoneId: null,
      isOnsite: false,
      onsiteLocation: '',
      raci: {
        responsible: [],
        accountable: '',
        consulted: [],
        informed: [],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset once per open, not when users/project change
  }, [open, isEditMode]);

  // Populate edit form after RACI users finish loading
  useEffect(() => {
    if (!open || !isEditMode || !initialData) return;

    const task = initialData as unknown as Task;
    const stillLoading = usersLoading || (taskRaciUserIds.length > 0 && additionalUsersLoading);
    if (stillLoading || users.length === 0) return;

    reset({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || TaskPriority.P3,
      category: task.category || undefined,
      startDate: task.startDate ? dayjs(task.startDate) : null,
      dueDate: task.dueDate ? dayjs(task.dueDate) : dayjs().add(1, 'day'),
      estimatedTime: task.estimatedMinutes || 60,
      projectId: task.projectId || initialProjectId || null,
      milestoneId: task.milestoneId || null,
      isOnsite: task.isOnsite || false,
      onsiteLocation: task.onsiteLocation || '',
      raci: {
        responsible: task.raci?.responsible || [],
        accountable: task.raci?.accountable || '',
        consulted: task.raci?.consulted || [],
        informed: task.raci?.informed || [],
      },
    });
  }, [
    open,
    isEditMode,
    initialData,
    initialProjectId,
    users,
    usersLoading,
    additionalUsersLoading,
    taskRaciUserIds,
    reset,
  ]);

  // Create task mutation  
  const createMutation = useMutation({
    mutationFn: async (data: Partial<Task>) => {
      // Create parent task first
      const parentTask = await taskService.createTask(data);
      
      // Create subtasks with parentTaskId if any
      if (subtasks.length > 0 && !isEditMode) {
        const subtaskPromises = subtasks.map(async (subtask) => {
          try {
            return await taskService.createTask({
              title: subtask.title,
              description: '',
              priority: data.priority, // Inherit from parent
              category: 'subtask' as TaskCategory, // Mark as subtask
              startDate: data.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : undefined,
              dueDate: subtask.dueDate ? dayjs(subtask.dueDate).format('YYYY-MM-DD') : data.dueDate,
              estimatedMinutes: subtask.estimatedTime || undefined, // Send minutes directly
              projectId: data.projectId, // Inherit from parent
              milestoneId: data.milestoneId, // Inherit from parent
              parentTaskId: parentTask.id, // Link to parent
              raci: {
                responsible: subtask.responsible,
                accountable: data.raci?.accountable || '', // Inherit accountable from parent
                consulted: data.raci?.consulted || [], // Inherit from parent
                informed: data.raci?.informed || [], // Inherit from parent
              },
            });
          } catch (error: any) {
            // Show backend validation errors as snackbar
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create subtask';
            showError(errorMessage);
            throw error;
          }
        });
        
        const createdSubtasks = await Promise.all(subtaskPromises);
        const subtaskIds = createdSubtasks.map((st) => st.id).filter(Boolean);
        if (subtaskIds.length > 0) {
          await taskService.updateTask(parentTask.id, { subtaskIds });
        }
      }
      
      return parentTask;
    },
    onSuccess: async (parentTask) => {
      // Create recurrence config if enabled
      if (enableRecurrence && !isEditMode) {
        try {
          await taskService.createRecurrence(parentTask.id, {
            frequency: recurrenceFrequency,
            intervalValue: recurrenceInterval,
            daysOfWeek: recurrenceFrequency === RecurrenceFrequency.WEEKLY ? recurrenceDaysOfWeek : undefined,
            endType: recurrenceEndType,
            endValue: undefined, // Only "Never" supported in simple form
          });
          showSuccess('Task created with recurring pattern!');
        } catch (error: any) {
          showError('Task created, but failed to set recurrence: ' + (error?.message || 'Unknown error'));
        }
      } else {
        showSuccess(subtasks.length > 0 
          ? `Task created with ${subtasks.length} subtask(s)!` 
          : 'Task created successfully!');
      }

      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      
      if (createAndContinue) {
        // Reset form but keep sticky values
        handleResetForNextTask();
      } else {
        handleClose();
      }
      
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create task';
      showError(errorMessage);
    },
  });

  // Update task mutation
  const updateMutation = useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: Partial<Task> }) =>
      taskService.updateTask(taskId, data),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      // Invalidate specific task query to refresh detail view
      if (initialData) {
        const taskId = (initialData as unknown as Task).id;
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.task(taskId) });
        const previousResponsible = (initialData as unknown as Task).raci?.responsible ?? [];
        const nextResponsible = updatedTask.raci?.responsible ?? [];
        const responsibleChanged =
          JSON.stringify([...previousResponsible].sort()) !== JSON.stringify([...nextResponsible].sort());
        if (responsibleChanged) {
          dispatch(clearTimerForTask(taskId));
        }
      }
      showSuccess('Task updated successfully!');
      handleClose();
      onSuccess?.();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update task';
      showError(errorMessage);
    },
  });

  const handleClose = () => {
    reset();
    setSubtasks([]);
    setCreateAndContinue(false);
    setEnableRecurrence(false);
    setRecurrenceExpanded(false);
    setRecurrenceFrequency(RecurrenceFrequency.DAILY);
    setRecurrenceInterval(1);
    setRecurrenceDaysOfWeek([]);
    setRecurrenceEndType(RecurrenceEndType.NEVER);
    setEstimatedTimeUnit('hours');
    setEstimatedTimeDraft(null);
    onClose();
  };

  const handleResetForNextTask = () => {
    const currentValues = watch();
    
    // Reset form with sticky values
    reset({
      title: '', // Clear
      description: '', // Clear
      priority: currentValues.priority, // Keep priority
      category: currentValues.category, // Keep category
      startDate: currentValues.startDate, // Keep start date
      dueDate: dayjs(currentValues.dueDate).add(1, 'day'), // Increment due date by 1 day
      estimatedTime: 60, // Reset to default
      projectId: currentValues.projectId, // Keep project
      milestoneId: currentValues.milestoneId, // Keep milestone
      isOnsite: currentValues.isOnsite, // Keep onsite flag
      onsiteLocation: currentValues.onsiteLocation, // Keep location
      raci: currentValues.raci, // Keep RACI (sticky team)
    });
    
    setSubtasks([]);
    setCreateAndContinue(false);
    setEnableRecurrence(false);
    setRecurrenceExpanded(false);
    setRecurrenceFrequency(RecurrenceFrequency.DAILY);
    setRecurrenceInterval(1);
    setRecurrenceDaysOfWeek([]);
    setRecurrenceEndType(RecurrenceEndType.NEVER);
  };

  // Subtask handlers
  const handleSubtaskAdd = () => {
    const lastSubtaskDue = subtasks.length > 0 
      ? subtasks[subtasks.length - 1].dueDate 
      : watch('dueDate');
    
    setSubtasks([
      ...subtasks,
      {
        title: '',
        estimatedTime: 30, // Default 30 minutes
        dueDate: lastSubtaskDue,
        responsible: [],
      },
    ]);
    setSubtasksExpanded(true);
  };

  const handleSubtaskChange = (index: number, data: SubtaskData) => {
    const newSubtasks = [...subtasks];
    newSubtasks[index] = data;
    setSubtasks(newSubtasks);
  };

  const handleSubtaskRemove = (index: number) => {
    setSubtasks(subtasks.filter((_, i) => i !== index));
  };

  // Get available users from parent RACI for subtasks
  const parentRACIUsers = React.useMemo(() => {
    const raciValues = watch('raci');
    if (!raciValues) return [];
    
    const userIds = [
      ...(raciValues.responsible || []),
      ...(raciValues.consulted || []),
      ...(raciValues.informed || []),
      raciValues.accountable,
    ].filter((id): id is string => Boolean(id));
    
    const uniqueIds = Array.from(new Set(userIds));
    return users.filter((u) => uniqueIds.includes(u.id));
  }, [watch('raci'), users]);

  const onSubmit = (data: TaskFormData) => {
    if (
      enableRecurrence &&
      !isEditMode &&
      recurrenceFrequency === RecurrenceFrequency.WEEKLY &&
      recurrenceDaysOfWeek.length === 0
    ) {
      showError('Select at least one day for weekly recurrence');
      return;
    }

    const payload: Partial<Task> = {
      title: data.title,
      description: data.description || '',
      priority: data.priority,
      category: (data.category || undefined) as TaskCategory | undefined,
      startDate: data.startDate ? dayjs(data.startDate).format('YYYY-MM-DD') : undefined,
      dueDate: dayjs(data.dueDate).format('YYYY-MM-DD'),
      estimatedMinutes: data.estimatedTime || undefined, // Send minutes directly
      projectId: data.projectId || undefined,
      milestoneId: data.milestoneId || undefined,
      isOnsite: data.isOnsite || false,
      onsiteLocation: data.isOnsite ? data.onsiteLocation : undefined,
      raci: data.raci ? {
        responsible: data.raci.responsible || [],
        accountable: data.raci.accountable || '',
        consulted: data.raci.consulted || [],
        informed: data.raci.informed || [],
      } : undefined,
    };

    if (isEditMode && initialData) {
      // Update existing task
      const task = initialData as unknown as Task;
      updateMutation.mutate({ taskId: task.id, data: payload });
    } else {
      // Create new task
      createMutation.mutate(payload);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 },
        }}
      >
        <DialogTitle sx={{ px: isMobile ? 2 : 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton onClick={handleClose} edge="start" sx={{ ml: -1 }}>
                  <ArrowLeft size={20} />
                </IconButton>
                <Typography variant="subtitle1" fontWeight={600}>
                  {isEditMode ? 'Edit Task' : 'New Task'}
                </Typography>
              </Box>
            ) : (
              <Typography variant="h6" fontWeight={600}>
                {isEditMode ? 'Edit Task' : 'Create New Task'}
              </Typography>
            )}
            {!isMobile && (
              <IconButton onClick={handleClose} size="small">
                <X size={20} />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <Divider />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2.5 : 3 }}>
              {/* Title */}
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Task Title"
                    required
                    fullWidth
                    size={isMobile ? 'small' : 'medium'}
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    placeholder="Enter task title..."
                  />
                )}
              />

              {/* Description */}
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Describe the task..."
                  />
                )}
              />

              {/* Priority & Project Row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Priority */}
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <AppSelect
                      label="Priority *"
                      error={!!errors.priority}
                      helperText={errors.priority?.message}
                      {...field}
                    >
                        <AppMenuItem value={TaskPriority.P4}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#94a3b8',
                              }}
                            />
                            P4 - Low
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P3}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#4F46E5',
                              }}
                            />
                            P3 - Medium
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P2}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#f59e0b',
                              }}
                            />
                            P2 - High
                          </Box>
                        </AppMenuItem>
                        <AppMenuItem value={TaskPriority.P1}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#ef4444',
                              }}
                            />
                            P1 - Critical
                          </Box>
                        </AppMenuItem>
                    </AppSelect>
                  )}
                />

                {/* Project */}
                <Controller
                  name="projectId"
                  control={control}
                  render={({ field }) => (
                    <AppSelect
                      label="Project (Optional)"
                      {...field}
                      value={field.value || ''}
                    >
                        <AppMenuItem value="">
                          <em>No Project</em>
                        </AppMenuItem>
                        {projects.map((project: any) => (
                          <AppMenuItem key={project.id} value={project.id}>
                            {project.name}
                          </AppMenuItem>
                        ))}
                    </AppSelect>
                  )}
                />
              </Box>

              {/* Category */}
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <AppSelect
                        label="Category"
                        {...field}
                        value={field.value || ''}
                        disabled={categoriesLoading}
                      >
                          <AppMenuItem value="">
                            <em>No Category</em>
                          </AppMenuItem>
                          {taskCategories.map((item) => (
                            <AppMenuItem key={item.id} value={item.keyValue}>
                              {item.displayLabel}
                            </AppMenuItem>
                          ))}
                      </AppSelect>
                      {canManageCategories && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Settings2 size={14} />}
                          onClick={() => setManageCategoriesOpen(true)}
                          sx={{ mt: 0.5, whiteSpace: 'nowrap', minWidth: 'auto', px: 1.5 }}
                        >
                          Manage
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              />

              {/* Milestone - Only show when a project is selected */}
              {watch('projectId') && (() => {
                const selectedProject = projects.find((p: any) => p.id === watch('projectId'));
                const milestones: Milestone[] = selectedProject?.milestones || [];
                return milestones.length > 0 ? (
                  <Controller
                    name="milestoneId"
                    control={control}
                    render={({ field }) => (
                      <AppSelect
                        label="Milestone (Optional)"
                        {...field}
                        value={field.value || ''}
                      >
                          <AppMenuItem value="">
                            <em>No Milestone</em>
                          </AppMenuItem>
                          {milestones.map((milestone: Milestone) => (
                            <AppMenuItem key={milestone.id} value={milestone.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: milestone.isCompleted ? 'success.main' : 'warning.main',
                                  }}
                                />
                                {milestone.name}
                                {milestone.dueDate && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                    Due: {dayjs(milestone.dueDate).format('MMM D')}
                                  </Typography>
                                )}
                              </Box>
                            </AppMenuItem>
                          ))}
                      </AppSelect>
                    )}
                  />
                ) : null;
              })()}

              {/* Start Date & Due Date Row */}
              {projectDateRange && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'primary.50',
                    border: '1px solid',
                    borderColor: 'primary.100',
                    borderRadius: 1,
                  }}
                >
                  <Info size={16} style={{ marginTop: 2, flexShrink: 0, color: '#4F46E5' }} />
                  <Typography variant="caption" color="text.secondary">
                    Project window: <strong>{projectDateRange.label}</strong>.
                    Use this as a guide — you can still pick any task dates.
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Start Date */}
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Start Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText:
                            (errors.startDate?.message as string) ||
                            (projectDateRange?.start
                              ? `Project starts ${projectDateRange.start.format('MMM D, YYYY')}`
                              : undefined),
                        },
                      }}
                    />
                  )}
                />

                {/* Due Date */}
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Due Date *"
                      value={field.value}
                      onChange={field.onChange}
                      minDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.dueDate,
                          helperText:
                            (errors.dueDate?.message as string) ||
                            (projectDateRange?.end
                              ? `Project ends ${projectDateRange.end.format('MMM D, YYYY')}`
                              : undefined),
                        },
                      }}
                    />
                  )}
                />
              </Box>

              {/* Estimated Time Row */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Estimated Time
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={estimatedTimeUnit}
                    onChange={(_, value: EstimatedTimeUnit | null) => {
                      if (value) {
                        setEstimatedTimeUnit(value);
                        setEstimatedTimeDraft(null);
                      }
                    }}
                  >
                    <ToggleButton value="hours" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                      Hours
                    </ToggleButton>
                    <ToggleButton value="minutes" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                      Min
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Controller
                  name="estimatedTime"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value === undefined || value === null) return true;
                      if (value < 0) return 'Must be positive';
                      if (value > maxEstimatedMinutes) {
                        return estimatedTimeUnit === 'hours'
                          ? `Maximum ${maxEstimatedHours} hours allowed`
                          : `Maximum ${maxEstimatedMinutes} minutes allowed`;
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => {
                    const durationHint = formatMinutesAsHoursAndMinutes(field.value);
                    const helperText = errors.estimatedTime?.message
                      || (projectBudgetContext
                        ? `Project budget: ${Math.round((projectBudgetContext.estimated / 60) * 100) / 100}h · ${Math.round((projectBudgetContext.remaining / 60) * 100) / 100}h remaining`
                        : estimatedTimeUnit === 'hours'
                          ? (durationHint
                              ? `Equals ${durationHint} · type any hours (e.g. 1.5 or 18.17)`
                              : `Type any hours (e.g. 1.5 = 1h 30m) · max ${maxEstimatedHours}h`)
                          : `Max ${maxEstimatedMinutes} minutes`);

                    const displayValue =
                      estimatedTimeDraft !== null
                        ? estimatedTimeDraft
                        : formatMinutesForUnit(field.value) === ''
                          ? ''
                          : String(formatMinutesForUnit(field.value));

                    return (
                      <TextField
                        label={estimatedTimeUnit === 'hours' ? 'Hours' : 'Minutes'}
                        type="number"
                        fullWidth
                        value={displayValue}
                        error={!!errors.estimatedTime}
                        helperText={helperText}
                        InputProps={{
                          inputProps: {
                            min: 0,
                            step: estimatedTimeUnit === 'hours' ? 'any' : 1,
                          },
                          endAdornment: estimatedTimeUnit === 'hours' && durationHint ? (
                            <InputAdornment position="end">
                              <Typography variant="caption" color="text.secondary">
                                {durationHint}
                              </Typography>
                            </InputAdornment>
                          ) : undefined,
                        }}
                        onFocus={() => {
                          setEstimatedTimeDraft(
                            formatMinutesForUnit(field.value) === ''
                              ? ''
                              : String(formatMinutesForUnit(field.value))
                          );
                        }}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setEstimatedTimeDraft(raw);
                          // Allow empty / in-progress typing without snapping the old value back
                          if (raw.trim() === '' || raw === '.' || raw === '-' || raw === '-.') {
                            field.onChange(null);
                            return;
                          }
                          const parsed = parseInputToMinutes(raw);
                          if (parsed !== null) {
                            field.onChange(parsed);
                          }
                        }}
                        onBlur={() => {
                          field.onBlur();
                          if (estimatedTimeDraft !== null) {
                            const parsed = parseInputToMinutes(estimatedTimeDraft);
                            field.onChange(parsed);
                          }
                          setEstimatedTimeDraft(null);
                        }}
                      />
                    );
                  }}
                />
              </Box>

              {/* Recurring Task Section */}
              {!isEditMode && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      mb: 1,
                    }}
                    onClick={() => {
                      // Expand/collapse only — do not auto-enable recurrence
                      setRecurrenceExpanded(!recurrenceExpanded);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Repeat size={18} style={{ color: '#8b5cf6' }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Make Recurring
                      </Typography>
                      <Tooltip
                        title="Recurring tasks are created automatically on a schedule (daily, weekly, monthly, or yearly). The system generates new task copies based on the pattern you set here."
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                      >
                        <IconButton
                          size="small"
                          aria-label="What is a recurring task?"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: 'text.secondary', p: 0.25 }}
                        >
                          <Info size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <IconButton size="small">
                      {recurrenceExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </IconButton>
                  </Box>

                  <Collapse in={recurrenceExpanded}>
                    <Box sx={{ pl: 4, pr: 1, pb: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={enableRecurrence}
                            onChange={(e) => setEnableRecurrence(e.target.checked)}
                          />
                        }
                        label="Enable recurring task pattern"
                        sx={{ mb: 2 }}
                      />

                      {enableRecurrence && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Frequency Selector */}
                          <AppSelect
                            label="Frequency"
                            size="small"
                            value={recurrenceFrequency}
                            onChange={(e) => setRecurrenceFrequency(e.target.value as RecurrenceFrequency)}
                          >
                              <AppMenuItem value={RecurrenceFrequency.DAILY}>Daily</AppMenuItem>
                              <AppMenuItem value={RecurrenceFrequency.WEEKLY}>Weekly</AppMenuItem>
                              <AppMenuItem value={RecurrenceFrequency.MONTHLY}>Monthly</AppMenuItem>
                              <AppMenuItem value={RecurrenceFrequency.YEARLY}>Yearly</AppMenuItem>
                          </AppSelect>

                          {/* Interval */}
                          <TextField
                            label="Repeat every"
                            type="number"
                            size="small"
                            value={recurrenceInterval}
                            onChange={(e) => setRecurrenceInterval(Math.max(1, parseInt(e.target.value) || 1))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  {recurrenceFrequency === RecurrenceFrequency.DAILY && 'day(s)'}
                                  {recurrenceFrequency === RecurrenceFrequency.WEEKLY && 'week(s)'}
                                  {recurrenceFrequency === RecurrenceFrequency.MONTHLY && 'month(s)'}
                                  {recurrenceFrequency === RecurrenceFrequency.YEARLY && 'year(s)'}
                                </InputAdornment>
                              ),
                            }}
                          />

                          {/* Days of Week (for weekly) */}
                          {recurrenceFrequency === RecurrenceFrequency.WEEKLY && (
                            <Box>
                              <Typography variant="body2" gutterBottom>
                                On days:
                              </Typography>
                              <ToggleButtonGroup
                                value={recurrenceDaysOfWeek}
                                onChange={(_, newDays) => setRecurrenceDaysOfWeek(newDays)}
                                size="small"
                                sx={{ flexWrap: 'wrap', gap: 0.5 }}
                              >
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                                  <ToggleButton key={idx} value={idx} sx={{ px: { xs: 1, sm: 2 } }}>
                                    {day}
                                  </ToggleButton>
                                ))}
                              </ToggleButtonGroup>
                            </Box>
                          )}

                          {/* End Type */}
                          <AppSelect
                            label="Ends"
                            size="small"
                            value={recurrenceEndType}
                            onChange={(e) => setRecurrenceEndType(e.target.value as RecurrenceEndType)}
                          >
                              <AppMenuItem value={RecurrenceEndType.NEVER}>Never</AppMenuItem>
                          </AppSelect>

                          {/* Preview */}
                          <Box sx={{ bgcolor: 'grey.50', p: 1.5, borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                              Preview:
                            </Typography>
                            <Typography variant="body2">
                              {recurrenceFrequency === RecurrenceFrequency.DAILY && 
                                `Repeats ${recurrenceInterval === 1 ? 'daily' : `every ${recurrenceInterval} days`}`}
                              {recurrenceFrequency === RecurrenceFrequency.WEEKLY && 
                                `Repeats ${recurrenceInterval === 1 ? 'weekly' : `every ${recurrenceInterval} weeks`}${
                                  recurrenceDaysOfWeek.length > 0 
                                    ? ' on ' + recurrenceDaysOfWeek.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')
                                    : ''}`}
                              {recurrenceFrequency === RecurrenceFrequency.MONTHLY && 
                                `Repeats ${recurrenceInterval === 1 ? 'monthly' : `every ${recurrenceInterval} months`}`}
                              {recurrenceFrequency === RecurrenceFrequency.YEARLY && 
                                `Repeats ${recurrenceInterval === 1 ? 'yearly' : `every ${recurrenceInterval} years`}`}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              {/* Onsite Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Location
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Indicate if this task requires onsite presence
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Controller
                    name="isOnsite"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Is Onsite"
                      />
                    )}
                  />

                  {watch('isOnsite') && (
                    <Controller
                      name="onsiteLocation"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Onsite Location"
                          fullWidth
                          size="small"
                          error={!!errors.onsiteLocation}
                          helperText={errors.onsiteLocation?.message}
                          placeholder="Enter location (e.g., Client Office, Site A)"
                        />
                      )}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* RACI Section */}
              <Typography variant="subtitle1" fontWeight={600}>
                RACI Assignment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -2 }}>
                Assign responsibility levels for this task
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {/* Responsible */}
                <Controller
                  name="raci.responsible"
                  control={control}
                  render={({ field }) => {
                    const selectedUsers = users.filter((u) => (field.value || []).includes(u.id));
                    return (
                      <Autocomplete
                        multiple
                        options={users}
                        getOptionLabel={(option) => userDisplayName(option)}
                        value={selectedUsers}
                        onChange={(_, newValue) =>
                          field.onChange(newValue.map((u) => u.id))
                        }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Responsible (R) *"
                          error={!!errors.raci?.responsible}
                          helperText={
                            errors.raci?.responsible?.message ||
                            'Who does the work'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="R"
                                  size="small"
                                  sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  );
                  }}
                />

                {/* Accountable */}
                <Controller
                  name="raci.accountable"
                  control={control}
                  render={({ field }) => {
                    const selectedUser = users.find((u) => u.id === field.value) || null;
                    return (
                      <Autocomplete
                        options={users}
                        getOptionLabel={(option) => userDisplayName(option)}
                        value={selectedUser}
                      onChange={(_, newValue) =>
                        field.onChange(newValue ? newValue.id : '')
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Accountable (A) *"
                          error={!!errors.raci?.accountable}
                          helperText={
                            errors.raci?.accountable?.message ||
                            'Single person who is answerable'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="A"
                                  size="small"
                                  sx={{
                                    bgcolor: 'warning.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  );
                  }}
                />

                {/* Consulted */}
                <Controller
                  name="raci.consulted"
                  control={control}
                  render={({ field }) => {
                    const selectedUsers = users.filter((u) => (field.value || []).includes(u.id));
                    return (
                      <Autocomplete
                        multiple
                        options={users}
                        getOptionLabel={(option) => userDisplayName(option)}
                        value={selectedUsers}
                        onChange={(_, newValue) =>
                          field.onChange(newValue.map((u) => u.id))
                        }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Consulted (C) *"
                          error={!!errors.raci?.consulted}
                          helperText={
                            errors.raci?.consulted?.message ||
                            'Who provides input (required)'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="C"
                                  size="small"
                                  sx={{
                                    bgcolor: 'info.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'info.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  );
                  }}
                />

                {/* Informed */}
                <Controller
                  name="raci.informed"
                  control={control}
                  render={({ field }) => {
                    const selectedUsers = users.filter((u) => (field.value || []).includes(u.id));
                    return (
                      <Autocomplete
                        multiple
                        options={users}
                        getOptionLabel={(option) => userDisplayName(option)}
                        value={selectedUsers}
                        onChange={(_, newValue) =>
                          field.onChange(newValue.map((u) => u.id))
                        }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Informed (I) *"
                          error={!!errors.raci?.informed}
                          helperText={
                            errors.raci?.informed?.message ||
                            'Who needs updates (required)'
                          }
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <Chip
                                  label="I"
                                  size="small"
                                  sx={{
                                    bgcolor: 'secondary.main',
                                    color: 'white',
                                    fontWeight: 600,
                                    mr: 1,
                                  }}
                                />
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            avatar={
                              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                {userDisplayName(option).charAt(0)}
                              </Avatar>
                            }
                            label={userDisplayName(option)}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                    />
                  );
                  }}
                />
              </Box>

              {/* Subtasks Section (only in create mode) */}
              {!isEditMode && (
                <SubtasksList
                  subtasks={subtasks}
                  onSubtaskAdd={handleSubtaskAdd}
                  onSubtaskChange={handleSubtaskChange}
                  onSubtaskRemove={handleSubtaskRemove}
                  parentEstimatedTime={watch('estimatedTime') || 60}
                  parentDueDate={watch('dueDate')}
                  parentStartDate={watch('startDate')}
                  availableUsers={parentRACIUsers}
                  expanded={subtasksExpanded}
                  onExpandToggle={() => setSubtasksExpanded(!subtasksExpanded)}
                />
              )}
            </Box>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: isMobile ? 2 : 3, py: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
            {isMobile ? (
              <>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isSubmitting || !isValid}
                  startIcon={<Plus size={16} />}
                  onClick={() => setCreateAndContinue(false)}
                >
                  {isSubmitting 
                    ? (isEditMode ? 'Saving...' : 'Creating...') 
                    : (isEditMode ? 'Save Changes' : 'Create Task')}
                </Button>
                {!isEditMode && (
                  <Button
                    type="submit"
                    variant="outlined"
                    fullWidth
                    disabled={isSubmitting || !isValid}
                    startIcon={<Plus size={16} />}
                    onClick={() => setCreateAndContinue(true)}
                  >
                    Create & Add Another
                  </Button>
                )}
                <Button onClick={handleClose} disabled={isSubmitting} fullWidth>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                {!isEditMode && (
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={isSubmitting || !isValid}
                    startIcon={<Plus size={16} />}
                    onClick={() => setCreateAndContinue(true)}
                    sx={{ mr: 1 }}
                  >
                    Create & Add Another
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !isValid}
                  startIcon={<Plus size={16} />}
                  onClick={() => setCreateAndContinue(false)}
                >
                  {isSubmitting 
                    ? (isEditMode ? 'Saving...' : 'Creating...') 
                    : (isEditMode ? 'Save Changes' : 'Create Task')}
                </Button>
              </>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <ManageTaskCategoriesModal
        open={manageCategoriesOpen}
        onClose={() => setManageCategoriesOpen(false)}
        onCategoryAdded={(keyValue) => {
          setValue('category', keyValue);
          showSuccess('Category added and selected');
        }}
      />
    </LocalizationProvider>
  );
};

export default CreateTaskModal;
